<template>
  <div ref="wrapper" data-test="us-accordion" class="us-accordion">
    <div
      v-for="(accordion, i) in accordions"
      :key="i"
      :class="['us-accordion__item', i === openIndex ? 'open' : '']"
    >
      <button class="us-accordion__title display--d2--semibold">
        <component
          :is="headingTag"
          v-if="accordion.heading"
          class="us-accordion__heading"
        >
          {{ accordion.heading }}
        </component>
        <slot
          v-else
          :name="`heading-slot-${i}`"
          class="us-accordion__heading"
        />
        <USIcon
          :diameter="20"
          name="chevron-up"
          class="us-accordion__open-icon"
        />
        <USIcon
          :diameter="20"
          name="chevron-down"
          class="us-accordion__close-icon"
        />
      </button>
      <div class="us-accordion__description">
        <p v-if="accordion.content">
          {{ accordion.content }}
        </p>
        <slot v-else :name="`accordion-${i}`" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  onUpdated,
  PropType,
  ref
} from 'vue'

import { Accordion } from '@base/@motiv/fe-patterns'

import USIcon from '@base/components/USIcon/USIcon.vue'

interface Action {
  heading: string
  content: string
}

export default defineComponent({
  name: 'USAccordion',
  components: { USIcon },
  props: {
    accordions: { type: Array as PropType<Array<Action>>, required: true },
    exclusive: { type: Boolean, default: true },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 3
    },
    openIndex: {
      type: Number,
      default: -1
    }
  },
  setup(props) {
    let wrapper = ref<HTMLElement>()
    const headingTag = computed(() => `h${props.headingLevel}`)
    let accordion: Accordion | null = null

    onMounted(() => {
      if (!wrapper.value) return
      accordion = new Accordion(
        wrapper.value,
        '.us-accordion__item',
        '.us-accordion__title',
        '.us-accordion__description',
        props.exclusive
      )
    })

    onUpdated(() => {
      if (accordion) {
        accordion.destory()
        accordion = null
      }

      if (!wrapper.value) return
      accordion = new Accordion(
        wrapper.value,
        '.us-accordion__item',
        '.us-accordion__title',
        '.us-accordion__description',
        props.exclusive
      )
    })

    onUnmounted(() => {
      accordion?.destory() // Spelling error with FE patterns
      accordion = null
    })

    return {
      headingTag,
      wrapper
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

@use '@base/@motiv/fe-patterns/styles/accordion';

@include accordion.accordion(
  $accordion-selector: '.us-accordion',
  $item-selector: '.us-accordion__item',
  $title-selector: '.us-accordion__title',
  $description-selector: '.us-accordion__description',
  $close-icon-selector: '.us-accordion__close-icon',
  $open-icon-selector: '.us-accordion__open-icon',
  $icon-size: --iconSize,
  $icon-top-offset: 0.1em,
  $transition-speed: var(--animation-speed-medium)
);

.us-accordion {
  --iconSize: 20px;

  color: var(--color--neutral-a1);

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    --iconSize: 24px;
  }

  &__item {
    border-bottom: var(--line-weight--heavy) solid var(--color--primary-b1);
    padding-top: var(--spacing--5);

    &.open {
      .us-accordion__description {
        margin-bottom: var(--spacing--6);
      }
    }
  }

  &__title {
    padding-bottom: var(--spacing--7);
    padding-right: var(--spacing--6);
  }

  &__heading {
    max-width: 90%;
  }

  &__description {
    font-family: var(--font-family--poppins);
    font-size: var(--font-size--z7);
    font-weight: var(--font-weight--regular);
    letter-spacing: var(--letter-spacing--ls0);
    line-height: var(--line-height--default);
  }
}

:deep(.us-icon) {
  color: var(--color--primary-b1);
}
</style>
